import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FormDropdown,
  NumberInput,
  PrevNextButton,
  WorkingHoursTimetable,
  MessageBox,
} from "reusableComponents";

import styles from "./styles.module.scss";
import {
  fetchSpaceDataWithAuth,
  submitStep3,
} from "services/addSpace.services";
import { useLanguage } from "context";
import { useSelector } from "react-redux";
import { selectAuthToken } from "store/auth/selectors";
import { formatWorkingHours } from "./helpers";

const Step3 = ({ space, setSpace, rootSpace, setRootSpace }) => {
  const { t, i18n } = useTranslation("global");
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const { language } = useLanguage();
  const accessToken = useSelector(selectAuthToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [touchedFields, setTouchedFields] = useState({});

  const [formData, setFormData] = useState({
    price_hour: null,
    price_half_day: null,
    price_day: null,
    price_month: null,
    minimal_capacity: null,
    placeUsage: null,
    minimal_duration: null,
  });

  // Get translated short day names using Intl.DateTimeFormat
  const getTranslatedShortDayNames = () => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
    
    // Create dates for each day of the week (Sunday to Saturday)
    // Using March 31, 2024 which was a Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(2024, 2, 31); // March 31, 2024 (Sunday)
      date.setDate(date.getDate() + i); // This gives us: Sun, Mon, Tue, Wed, Thu, Fri, Sat
      
      const dayName = formatter.format(date);
      return dayName.charAt(0).toUpperCase() + dayName.slice(1);
    });
  };

  // Get translated long day names (for tooltips or accessibility)
  const getTranslatedLongDayNames = () => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'long' });
    
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(2024, 2, 31); // March 31, 2024 (Sunday)
      date.setDate(date.getDate() + i);
      
      const dayName = formatter.format(date);
      return dayName.charAt(0).toUpperCase() + dayName.slice(1);
    });
  };

  // Create default working hours with translated day names
  const createDefaultWorkingHours = () => {
    const shortDayNames = getTranslatedShortDayNames();
    
    return Array.from({ length: 7 }, (_, i) => ({
      name: shortDayNames[i],
      slots: "0".repeat(48),
      isWorkingDay: false,
    })).reduce((acc, day, index) => {
      // Keep day_0 as Sunday, day_1 as Monday, etc.
      acc[`day_${index}`] = day;
      return acc;
    }, {});
  };

  const [workingHours, setWorkingHours] = useState(createDefaultWorkingHours());

  const isSeminar = formData.placeUsage?.key === "seminar" || 
                    formData.placeUsage?.key === "teambuilding";

  // Update working hours day names when language changes
  useEffect(() => {
    setWorkingHours(prev => {
      const shortDayNames = getTranslatedShortDayNames();
      const updated = { ...prev };
      
      Object.keys(prev).forEach((key, index) => {
        if (updated[key]) {
          updated[key] = {
            ...updated[key],
            name: shortDayNames[index]
          };
        }
      });
      
      return updated;
    });
  }, [i18n.language]);

  // Mark field as touched when user interacts
  const markFieldTouched = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  // Check if field is required based on conditions
const isFieldRequired = (fieldName) => {
  if (fieldName === "minimal_duration") {
    return true; // Always required
  }

  if (fieldName === "minimal_capacity") {
    return isSeminar; // Only required for seminar
  }

  if (fieldName === "workingHours") {
    return true; // Always required
  }

  if (fieldName.startsWith("price_")) {
    const priceType = fieldName.replace("price_", "");
    const minDuration = formData.minimal_duration;
    
    if (!minDuration) return false;

    const requiredMap = {
      hour: ["hour"],
      half_day: ["hour", "half_day"],
      day: ["day"], // ← This was correct: only "day" requires price_day
      month: ["month"]
    };

    return requiredMap[priceType]?.includes(minDuration) || false;
  }

  return false;
};

  // Check if a value is valid (not empty, not null, not undefined, and > 0 for numbers)
  const isValidValue = (value) => {
    if (value === null || value === undefined || value === "") return false;
    if (typeof value === "number" && value <= 0) return false;
    if (typeof value === "string" && value.trim() === "") return false;
    return true;
  };

  // Get dropdown options based on space type
  const getDurationOptions = () => {
    if (isSeminar) {
      return [
        { value: "half_day", label: t("provide.step3.price.duration.halfDay") },
        { value: "day", label: t("provide.step3.price.duration.day") },
        { value: "residential", label: t("provide.step3.price.duration.residential") },
      ];
    } else {
      return [
        { value: "hour", label: t("provide.step3.price.duration.hour") },
        { value: "half_day", label: t("provide.step3.price.duration.halfDay") },
        { value: "day", label: t("provide.step3.price.duration.day") },
        { value: "month", label: t("provide.step3.price.duration.month") },
      ];
    }
  };

  // Handle input changes with proper null/empty handling
  const handleInputChange = (key, value) => {
    // Mark field as touched
    markFieldTouched(key);
    
    // For number inputs, convert empty string to null
    let processedValue = value;
    
    if (key.startsWith('price_') || key === 'minimal_capacity') {
      processedValue = value === "" ? null : Number(value);
    }
    
    setFormData((prev) => ({ ...prev, [key]: processedValue }));
    
    // Clear validation error for this field when user interacts
    setValidationErrors(prev => prev.filter(error => error.field !== key));
  };

  // Helper function to get error message for a specific field
  const getFieldError = (fieldName) => {
    const error = validationErrors.find(err => err.field === fieldName);
    return error ? error.message : "";
  };

  // Check if field should show error message
  const shouldShowError = (fieldName) => {
    const isRequired = isFieldRequired(fieldName);
    const isTouched = touchedFields[fieldName];
    const value = fieldName === "workingHours" 
      ? workingHours 
      : formData[fieldName];
    
    if (!isRequired) return false;
    if (!isTouched) return false;
    
    // Special handling for working hours
    if (fieldName === "workingHours") {
      const hasWorkingHours = Object.values(workingHours).some(
        (day) => day.isWorkingDay && day.slots.includes("1")
      );
      return !hasWorkingHours;
    }
    
    // For all other fields, check if value is valid
    return !isValidValue(value);
  };

  // Get the appropriate error message for a field
  const getErrorMessage = (fieldName) => {
    if (!shouldShowError(fieldName)) return null;
    
    const value = fieldName === "workingHours" 
      ? workingHours 
      : formData[fieldName];
    
    // Special message for working hours
    if (fieldName === "workingHours") {
      return t("validation.workingHours.required", "Please set working hours for at least one day");
    }
    
    // Check if value is 0 specifically
    if (value === 0 || value === "0") {
      return t("validation.valueMustBePositive", "Value must be greater than 0");
    }
    
    // Generic required message for empty fields
    return t("validation.fieldRequired", "This field is required");
  };

  // Get price inputs based on space type and required status
  const getPriceInputs = () => {
    if (isSeminar) {
      return (
        <div className={styles.inputWrapper}>
          <NumberInput
            onChange={(e) => handleInputChange("minimal_capacity", e.target.value)}
            onBlur={() => markFieldTouched("minimal_capacity")}
            value={formData.minimal_capacity ?? ""}
            labelAbove
            required
            min={1}
            label={t("provide.step3.price.minimalCapacity")}
          />
          {getErrorMessage("minimal_capacity") && (
            <MessageBox
              type="error"
              message={getErrorMessage("minimal_capacity")}
            />
          )}
        </div>
      );
    } else {
      return ["hour", "half_day", "day", "month"].map((price) => {
        const fieldName = `price_${price}`;
        const isRequired = isFieldRequired(fieldName);

        return (
          <div key={price} className={styles.inputWrapper}>
            <NumberInput
              onChange={(e) => handleInputChange(fieldName, e.target.value)}
              onBlur={() => markFieldTouched(fieldName)}
              addonText={t("provide.step3.price.unit")}
              value={formData[fieldName] ?? ""}
              labelAbove
              required={isRequired}
              min={0}
              step={1}
              label={t(
                `provide.step3.price.${
                  price === "half_day" ? "halfDay" : price
                }`
              )}
            />
            {getErrorMessage(fieldName) && (
              <MessageBox
                type="error"
                message={getErrorMessage(fieldName)}
              />
            )}
          </div>
        );
      });
    }
  };

  // Fetch existing data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetchSpaceDataWithAuth(spaceId, accessToken, language);
        if (!data) return;

        setFormData((prev) => {
          const updatedData = { 
            ...data, 
            place_id: data.id,
            // Ensure price fields are properly set from API response
            price_hour: data.price_hour ?? null,
            price_half_day: data.price_half_day ?? null,
            price_day: data.price_day ?? null,
            price_month: data.price_month ?? null,
            minimal_capacity: data.minimal_capacity ?? null,
          };

          // Set default minimal_duration based on space type if not present
          if (!updatedData.minimal_duration) {
            updatedData.minimal_duration = isSeminar ? "half_day" : "hour";
          }

          return updatedData;
        });

        // Get current translated day names
        const shortDayNames = getTranslatedShortDayNames();

        // Set working hours from API response with translated day names
        setWorkingHours(
          Object.fromEntries(
            Object.entries(createDefaultWorkingHours()).map(([key, val], index) => {
              const slots = data[key] ?? "0".repeat(48);
              return [
                key,
                {
                  ...val,
                  name: shortDayNames[index],
                  slots,
                  isWorkingDay: slots.includes("1"),
                },
              ];
            })
          )
        );
      } catch (error) {
        console.error("Error fetching space data:", error);
        setError(t("errors.fetchFailed", "Failed to load space data"));
      } finally {
        setLoading(false);
      }
    };

    if (spaceId) {
      fetchData();
    }
  }, [spaceId, accessToken, language, isSeminar, t]);

  // Validate form before submission
const validateForm = () => {
  // Mark all required fields as touched
  const allFields = [
    "minimal_duration",
    "workingHours",
    ...(isSeminar ? ["minimal_capacity"] : []),
    ...(isSeminar ? [] : ["price_hour", "price_half_day", "price_day", "price_month"])
  ];
  
  const touchedState = {};
  allFields.forEach(field => {
    touchedState[field] = true;
  });
  setTouchedFields(touchedState);

  // Check working hours
  const hasWorkingHours = Object.values(workingHours).some(
    (day) => day.isWorkingDay && day.slots.includes("1")
  );

  const errors = [];

  if (!hasWorkingHours) {
    errors.push({
      field: "workingHours",
      message: t("validation.workingHours.required", "Please set working hours for at least one day")
    });
  }

  if (!formData.minimal_duration) {
    errors.push({
      field: "minimal_duration",
      message: t("validation.fieldRequired", "This field is required")
    });
  }

  if (isSeminar) {
    const capacity = formData.minimal_capacity;
    if (!capacity || capacity <= 0) {
      errors.push({
        field: "minimal_capacity",
        message: capacity === 0 
          ? t("validation.valueMustBePositive", "Value must be greater than 0")
          : t("validation.fieldRequired", "This field is required")
      });
    }
  } else {
    const minDuration = formData.minimal_duration;
    
    const validatePrice = (priceField) => {
      const price = formData[priceField];
      if (price === null || price === undefined || price === "" || price <= 0) {
        errors.push({
          field: priceField,
          message: price === 0 
            ? t("validation.valueMustBePositive", "Value must be greater than 0")
            : t("validation.fieldRequired", "This field is required")
        });
      }
    };

    // Fix: Only validate the price fields that are actually required
    if (minDuration === "hour") {
      validatePrice("price_hour");
            validatePrice("price_half_day");
    } else if (minDuration === "half_day") {
      validatePrice("price_half_day");
    } else if (minDuration === "day") {
      validatePrice("price_day"); // ← Only validate price_day for "day" duration
    } else if (minDuration === "month") {
      validatePrice("price_month");
    }
  }

  setValidationErrors(errors);
  return errors.length === 0;
};

  // Handle form submission
  const handleSubmit = async (direction) => {
    setLoading(true);
    setError("");

    if (direction === "prev") {
      navigate(`/create-space/step2/${spaceId}`);
      setLoading(false);
      return;
    }

    // Validate all required fields
    if (!validateForm()) {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      // Prepare payload for submission
      const payload = {
        place_id: spaceId,
        minimal_duration: formData.minimal_duration,
        price_hour: formData.price_hour ? Number(formData.price_hour) : 0,
        price_half_day: formData.price_half_day ? Number(formData.price_half_day) : 0,
        price_day: formData.price_day ? Number(formData.price_day) : 0,
        price_month: formData.price_month ? Number(formData.price_month) : 0,
        minimal_capacity: formData.minimal_capacity ? Number(formData.minimal_capacity) : null,
        opening_hours: formatWorkingHours(workingHours),
      };

      await submitStep3(payload, spaceId, language, accessToken);
      navigate(`/create-space/step4/${spaceId}`);
    } catch (error) {
      setError(error.message || t("errors.submitFailed", "Failed to submit form"));
      console.error("Error submitting Step 3:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={loading ? styles.loading : ""}>
      {/* Mobile navigation */}
      <PrevNextButton
        color="nav"
        onNext={() => handleSubmit("next")}
        onPrev={() => handleSubmit("prev")}
        isIconOnly
      />
      
      {/* Summary error message */}
      {validationErrors.length > 0 && (
        <MessageBox
          type="error"
          message={t("validation.formHasErrors", "Please fix the errors below before proceeding")}
        />
      )}
      
      {/* Error message from API */}
      {/* {error && (
        <MessageBox
          type="error"
          message={error}
        />
      )} */}
      
      <section>
        <h4 className={styles.subtitle}>
          {t("provide.step3.timetable.title")}
        </h4>
        
        <WorkingHoursTimetable
          workingHours={workingHours}
          setWorkingHours={setWorkingHours}
        />
        
        {getErrorMessage("workingHours") && (
          <MessageBox
            type="error"
            message={getErrorMessage("workingHours")}
          />
        )}
        
        <h4 className={styles.subtitle}>{t("provide.step3.price.title")}</h4>
        
        <div className={styles.inputWrapper}>
          <FormDropdown
            labelAbove
            required
            onSelect={(value) => {
              handleInputChange("minimal_duration", value);
              // Clear price validation errors when duration changes
              setValidationErrors(prev => prev.filter(err => 
                !err.field.startsWith("price_") && err.field !== "minimal_capacity"
              ));
            }}
            onBlur={() => markFieldTouched("minimal_duration")}
            selected={formData.minimal_duration}
            options={getDurationOptions()}
            label={t("provide.step3.price.minimalDuration")}
          />
          
          {getErrorMessage("minimal_duration") && (
            <MessageBox
              type="error"
              message={getErrorMessage("minimal_duration")}
            />
          )}
        </div>

        <div className={styles.inputsGroup}>
          {getPriceInputs()}
        </div>
      </section>

      {/* Desktop navigation */}
      <div className={styles.nextBtnContainer}>
        <PrevNextButton
          color="nav"
          onNext={() => handleSubmit("next")}
          onPrev={() => handleSubmit("prev")}
        />
      </div>
    </div>
  );
};

export default Step3;